import React, { useState, useRef, useCallback } from 'react';
import cx from 'classnames';
import { ReactionButtonContainer } from './ReactionButtonContainer';
import { VideoPlayer, VideoPlayerPlayPausePlacement } from '@vimily/bonjoro-ui-components-v2/VideoPlayer';
import { useDispatch, useSelector } from 'react-redux';
import { videoPlayView } from '../actions';
import { isVideoWatched } from '~/utils/video';
import { ReduxState } from '~/types/redux/share';
import { Transcript } from '~/types/models';
import { colors } from '@vimily/bonjoro-ui-components-v2/dist/theme';
import {
  addUtmToLink,
  UTM_SOURCE_GREET_SHARE_PAGE,
  UTM_CAMPAIGN_GREET_REFERRALS,
  UTM_MEDIUM_HEADER_BADGE,
} from '~/utils/add-utm-to-link';
import { BonjoroBadgeMobile } from '~/App/Components/BonjoroBadgeMobile';

export type PlayerProps = {
  videoUrl: string;
  thumbUrl?: string;
  showReactionButton?: boolean;
  videoPlayerContainerClassname?: string;
  videoClassName?: string;
  thumbnailClassName?: string;
  preload?: 'metadata' | 'auto' | 'none';
  primaryColor?: string;
  transcript?: Transcript;
  showBonjoroBadge?: boolean;
};

export default function Player({
  videoUrl,
  thumbUrl,
  showReactionButton,
  videoPlayerContainerClassname,
  videoClassName,
  thumbnailClassName,
  preload,
  primaryColor = colors.white,
  transcript,
  showBonjoroBadge = false,
}: PlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [watched, setWatched] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const dispatch = useDispatch();
  const { videoWatched } = useSelector((state: ReduxState) => ({
    videoWatched: state.tracking.videoWatched,
  }));

  const markVideoAsWatched = () => {
    dispatch(videoPlayView());
    videoWatched?.();
    setWatched(true);
  };

  const checkVideoWatchTime = useCallback(() => {
    if (!videoRef.current || videoRef.current?.paused) return;

    if (isVideoWatched(videoRef?.current) && !watched) {
      markVideoAsWatched();
    }
  }, [watched, videoRef]);

  const handleImageClick = () => {
    videoRef.current?.play();
    setPlaying(true);
  };

  return (
    <div className='video-container relative'>
      {!playing && thumbUrl && !videoLoaded && (
        <img
          src={thumbUrl}
          className={cx(
            thumbnailClassName,
            'w-full rounded absolute top-0 left-0 v2-z-10 cursor-pointer',
            {
              hidden: playing,
            }
          )}
          onClick={handleImageClick}
        />
      )}
      <VideoPlayer
        videoRef={videoRef}
        leftHeaderComponent={
          showBonjoroBadge ? (
            <BonjoroBadgeMobile
              url={addUtmToLink('/', {
                utm_source: UTM_SOURCE_GREET_SHARE_PAGE,
                utm_medium: UTM_MEDIUM_HEADER_BADGE,
                utm_campaign: UTM_CAMPAIGN_GREET_REFERRALS,
              })}
            />
          ) : undefined
        }
        containerClassName={videoPlayerContainerClassname}
        videoClassName={cx(videoClassName, 'w-full bg-white')}
        videoUrl={videoUrl}
        videoThumbnail={thumbUrl}
        onProgress={checkVideoWatchTime}
        hideClose
        hideMuteUnmute
        hideNewTab
        rounded
        showProgressBar
        showDefaultFullScreen
        preload={preload}
        hideLoader
        transcription={transcript?.captions?.sentences}
        onPlay={() => setPlaying(true)}
        playPauseStyle={{ color: primaryColor }}
        onVideoLoaded={() => setVideoLoaded(true)}
        playPausePlacement={VideoPlayerPlayPausePlacement.Center}
      />
      {showReactionButton && videoLoaded && <ReactionButtonContainer />}
    </div>
  );
}
